import React, { useEffect, useMemo, useRef, useState } from "react";
import Graph from "react-vis-network-graph";
import LoadingSpinner from "../../../pages/dashboard/LoadingSpinner";

const SD_BREAKPOINTS = {
  green: 0,
  yellow: 20,
  orange: 35,
  red: 50,
};

const MIN_NODE_SIZE = 15;
const MAX_NODE_SIZE = 100;

const GRADIENT_STOPS = [
  { t: 0.0, rgb: [40, 167, 69] },   // green  #28a745
  { t: 0.33, rgb: [255, 193, 7] },  // yellow #ffc107
  { t: 0.66, rgb: [253, 126, 20] }, // orange #fd7e14
  { t: 1.0, rgb: [220, 53, 69] },    // red    #dc3545
];

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const lerp = (a, b, t) => a + (b - a) * t;

function rgbToHex([r, g, b]) {
  const toHex = (x) => x.toString(16).padStart(2, "0");
  return `#${toHex(Math.round(r))}${toHex(Math.round(g))}${toHex(Math.round(b))}`;
}

function hexToRgb(hex) {
  const h = (hex || "").replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  if (!Number.isFinite(n)) return [0, 0, 0];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function lightenHex(hex, amount = 0.35) {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex([
    lerp(r, 255, clamp(amount, 0, 1)),
    lerp(g, 255, clamp(amount, 0, 1)),
    lerp(b, 255, clamp(amount, 0, 1)),
  ]);
}

function sdToGradientT(sd) {
  const v = Math.max(0, Number(sd) || 0);
  const { yellow, orange, red } = SD_BREAKPOINTS;

  if (v <= yellow) {
    const local = v / (yellow || 1);
    return lerp(0.0, 0.33, clamp(local, 0, 1));
  }
  if (v <= orange) {
    const local = (v - yellow) / ((orange - yellow) || 1);
    return lerp(0.33, 0.66, clamp(local, 0, 1));
  }
  const local = (v - orange) / ((red - orange) || 1);
  return lerp(0.66, 1.0, clamp(local, 0, 1));
}

function darkenHex(hex, amount = 0.25) {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex([
    lerp(r, 0, clamp(amount, 0, 1)),
    lerp(g, 0, clamp(amount, 0, 1)),
    lerp(b, 0, clamp(amount, 0, 1)),
  ]);
}

function gradientColor(t) {
  const tt = clamp(t, 0, 1);
  for (let i = 0; i < GRADIENT_STOPS.length - 1; i++) {
    const a = GRADIENT_STOPS[i];
    const b = GRADIENT_STOPS[i + 1];
    if (tt >= a.t && tt <= b.t) {
      const localT = (tt - a.t) / (b.t - a.t || 1);
      return rgbToHex([
        lerp(a.rgb[0], b.rgb[0], localT),
        lerp(a.rgb[1], b.rgb[1], localT),
        lerp(a.rgb[2], b.rgb[2], localT),
      ]);
    }
  }
  return rgbToHex(GRADIENT_STOPS[GRADIENT_STOPS.length - 1].rgb);
}

function hexToRgba(hex, alpha = 1) {
  const [r, g, b] = hexToRgb(hex);
  const a = clamp(alpha, 0, 1);
  return `rgba(${r},${g},${b},${a})`;
}

function makeLeftToRightShadedCircleDataUri(baseColorHex, borderHex, opacity = 1) {
  const dark = darkenHex(baseColorHex, 0.30);
  const light = lightenHex(baseColorHex, 0.45);
  const o = clamp(opacity, 0, 1);

  const darkRgba = hexToRgba(dark, o);
  const baseRgba = hexToRgba(baseColorHex, o);
  const lightRgba = hexToRgba(light, o);
  const borderRgba = hexToRgba(borderHex, o);

  const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
    <defs>
      <linearGradient id="shade" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stop-color="${darkRgba}"/>
        <stop offset="50%" stop-color="${baseRgba}"/>
        <stop offset="100%" stop-color="${lightRgba}"/>
      </linearGradient>
    </defs>
    <circle cx="60" cy="60" r="52" fill="url(#shade)"/>
    <circle cx="60" cy="60" r="52" fill="none" stroke="${borderRgba}" stroke-width="3"/>
  </svg>`.trim();

  const encoded = encodeURIComponent(svg).replace(/'/g, "%27").replace(/"/g, "%22");
  return `data:image/svg+xml;charset=utf-8,${encoded}`;
}

export default function WorldCup({
  graph,
  title,
  onNetworkReady,
  activeNodeIds = null,
  headerActions,
  resetViewSignal = 0,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const networkRef = useRef(null);
  const pinnedRef = useRef(new Set());

  const rawNodes = useMemo(() => (Array.isArray(graph?.nodes) ? graph.nodes : []), [graph]);
  const rawEdges = useMemo(() => (Array.isArray(graph?.edges) ? graph.edges : []), [graph]);

  const hasGraph = rawNodes.length > 0;

  useEffect(() => {
    if (hasGraph) {
      setIsLoading(true);
      const timeout = setTimeout(() => setIsLoading(false), 500);
      return () => clearTimeout(timeout);
    }
    setIsLoading(false);
  }, [hasGraph]);

  const { locMin, locMax } = useMemo(() => {
    const locs = rawNodes
      .map((n) => Number(n?.loc))
      .filter((v) => Number.isFinite(v) && v >= 0);

    if (locs.length === 0) return { locMin: 0, locMax: 1 };
    return { locMin: Math.min(...locs), locMax: Math.max(...locs) };
  }, [rawNodes]);

  const isActive = (id) => {
    if (!activeNodeIds || !(activeNodeIds instanceof Set)) return true;
    return activeNodeIds.has(id);
  };

  const nodes = useMemo(() => {
    const denom = (locMax - locMin) || 1;

    return rawNodes.map((node) => {
      const label = node?.label || node?.title || node?.id || "";
      const loc = Number(node?.loc);
      const sdRaw = Number(node?.sd);

      const locValue = Number.isFinite(loc) && loc >= 0 ? loc : null;
      const sdValue = Number.isFinite(sdRaw) && sdRaw >= 0 ? sdRaw : 0;

      let nodeSize = (MIN_NODE_SIZE + MAX_NODE_SIZE) / 2;
      if (locValue !== null) {
        const t = clamp((locValue - locMin) / denom, 0, 1);
        nodeSize = lerp(MIN_NODE_SIZE, MAX_NODE_SIZE, t);
      }

      const densityT = sdToGradientT(sdValue);
      const baseColorHex = gradientColor(densityT);
      const borderHex = darkenHex(baseColorHex, 0.30);

      const active = isActive(node.id);
      const opacity = active ? 1 : 0.12;

      const img = makeLeftToRightShadedCircleDataUri(baseColorHex, borderHex, opacity);

      return {
        ...node,
        size: nodeSize,
        shape: "circularImage",
        image: img,
        borderWidth: 0,
        borderWidthSelected: 0,

        // FIX: Allow node to be 'chosen' so selection/dragging works repeatedly,
        // but override the styles so no blue ring appears.
        chosen: {
          node: (values) => {
            values.borderWidth = 0;
            values.shadow = false;
          },
          label: false,
        },

        color: {
          border: hexToRgba(borderHex, opacity),
          background: hexToRgba(baseColorHex, opacity),
          highlight: { border: hexToRgba(borderHex, opacity), background: hexToRgba(baseColorHex, opacity) },
          hover: { border: hexToRgba(borderHex, opacity), background: hexToRgba(baseColorHex, opacity) },
        },

        font: {
          ...(node.font || {}),
          color: active ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.15)",
        },

        title: `
          <div style="max-width:320px">
            <div><b>${label}</b></div>
            <div><b>LOC:</b> ${locValue !== null ? locValue : "N/A"}</div>
            <div><b>Smell density:</b> ${sdValue}</div>
          </div>
        `,
      };
    });
  }, [rawNodes, locMin, locMax, activeNodeIds]);

  const edges = useMemo(() => {
    if (!activeNodeIds || !(activeNodeIds instanceof Set)) return rawEdges;

    return rawEdges.map((e) => {
      const from = e.from ?? e.source;
      const to = e.to ?? e.target;
      const active = activeNodeIds.has(from) && activeNodeIds.has(to);
      const opacity = active ? 1 : 0.10;

      return {
        ...e,
        opacity,
        color: { ...(e.color || {}), opacity },
      };
    });
  }, [rawEdges, activeNodeIds]);

  useEffect(() => {
    if (!networkRef.current) return;
    try {
      networkRef.current.unselectAll();
      networkRef.current.setOptions({
        interaction: {
          dragNodes: true,
          dragView: true,
          zoomView: true,
          selectable: true,
        },
      });
      networkRef.current.redraw();
      networkRef.current.fit({ animation: { duration: 250, easingFunction: "easeInOutQuad" } });
    } catch (e) {}
  }, [resetViewSignal]);

  if (!hasGraph) return <div />;

  const data = { nodes, edges };

  const options = {
    nodes: {
      font: { size: 12, face: "Tahoma" },
      borderWidth: 0,
      borderWidthSelected: 0,
    },
    edges: {
      width: 0.15,
      color: { inherit: "from" },
      smooth: { type: "continuous" },
    },
    physics: {
      enabled: true,
      barnesHut: {
        gravitationalConstant: -20000,
        centralGravity: 0.30,
        springLength: 160,
        springConstant: 0.03,
        damping: 0.25,
        avoidOverlap: 0.2,
      },
      stabilization: {
        enabled: true,
        iterations: 250,
        updateInterval: 50,
        fit: true,
      },
      timestep: 0.25,
      minVelocity: 0.05,
    },
    interaction: {
      navigationButtons: true,
      tooltipDelay: 200,
      hover: true,
      selectConnectedEdges: false,
      hoverConnectedEdges: false,
    },
    height: "900px",
  };

  return (
    <div className="card shadow-sm p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">{title}</h5>
        {headerActions && <div className="d-flex align-items-center gap-2">{headerActions}</div>}
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
          <LoadingSpinner title="Loading graph..." />
        </div>
      ) : (
        <Graph
          graph={data}
          options={options}
          getNetwork={(network) => {
            networkRef.current = network;

            if (!network.__hasPinHandlers) {
              network.__hasPinHandlers = true;

              network.on("dragEnd", (params) => {
                try {
                  const draggedIds = params?.nodes || [];
                  if (!draggedIds.length) return;

                  pinnedRef.current = pinnedRef.current || new Set();
                  draggedIds.forEach((id) => pinnedRef.current.add(id));

                  // FIX: Disable physics for the node to "pin" it. 
                  // This keeps it interactive for future clicks/drags.
                  const updates = draggedIds.map((id) => ({ 
                    id, 
                    physics: false 
                  }));
                  network.body.data.nodes.update(updates);
                } catch (e) {}
              });

              network.on("doubleClick", (params) => {
                try {
                  const id = params?.nodes?.[0];
                  if (!id) return;

                  pinnedRef.current.delete(id);
                  // FIX: Re-enable physics to unpin.
                  network.body.data.nodes.update([{ id, physics: true }]);
                } catch (e) {}
              });
            }

            if (typeof onNetworkReady === "function") {
              onNetworkReady(network);
            }
          }}
        />
      )}
    </div>
  );
}